import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gift, Share2, Star, X } from "lucide-react";
import Panel from "../../ui/Panel/Panel.jsx";
import Button from "../../ui/Button/Button.jsx";
import ShareBox from "../../share/ShareBox.jsx";
import FeedbackPanel from "../../feedback/FeedbackPanel.jsx";
import { dismissPrompt, getPendingFeedback, getReferralSummary } from "../../../services/engagementService";
import { money } from "../../../utils/format.js";

/**
 * Dashboard cards above "Recent filings":
 *  1. rate the most recently accepted return (not yet rated / not closed) - its ✕ closes
 *     that prompt for good (prompt_dismissal RATE_RETURN),
 *  2. share with your group - always shown, no ✕, highlighted so it stands apart from the
 *     plain panels. The credit balance itself is the Referral Credit stat card.
 * Any load failure just hides the cards - they are never in the way of filing.
 */
export default function EngagementCard() {
  const [loaded, setLoaded] = useState(false);
  const [pending, setPending] = useState(null);
  const [referral, setReferral] = useState(null);
  const [rating, setRating] = useState(false);

  useEffect(() => {
    let active = true;
    Promise.all([getPendingFeedback(), getReferralSummary()])
      .then(([p, r]) => {
        if (!active) return;
        setPending(p?.returnId ? p : null);
        setReferral(r || null);
        setLoaded(true);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  if (!loaded) return null;

  const closeRating = () => {
    const returnId = pending.returnId;
    setPending(null);
    dismissPrompt("RATE_RETURN", returnId).catch(() => {});
  };

  const icon = (Icon, cls = "bg-[var(--accent-soft)] text-[var(--accent)] grid") => (
    <div className={`w-10 h-10 rounded-[10px] ${cls} place-items-center flex-none`}>
      <Icon size={20} />
    </div>
  );

  return (
    <>
      {pending && (
        <Panel padded className="mb-4">
          <div className="flex items-start gap-3.5">
            {icon(Star)}
            <div className="flex-1 min-w-0">
              <div className="text-[15px] font-bold text-[var(--ink)]">Rate your last filing</div>
              <p className="text-[13px] text-[var(--muted)] m-0 mb-3">
                Form 2290 for <b>{pending.businessName}</b> was accepted by the IRS. How did it go?
              </p>
              <Button className="!h-9 !px-4 !text-[13px]" onClick={() => setRating(true)}>
                Rate now
              </Button>
            </div>
            <button
              type="button"
              aria-label="Close for good"
              title="Don't show this again"
              onClick={closeRating}
              className="text-[var(--muted)] hover:text-[var(--ink)] bg-transparent border-0 cursor-pointer p-1 flex-none"
            >
              <X size={18} />
            </button>
          </div>
        </Panel>
      )}

      <div className="mb-6 rounded-[var(--r)] border-2 border-[var(--accent)] bg-[var(--accent-soft)] p-4 sm:p-5">
        <div className="flex items-start gap-3.5">
          {icon(Share2, "bg-[var(--accent)] text-[var(--accent-ink)] hidden sm:grid")}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-[16px] font-bold text-[var(--ink)]">Know other truckers? Share EasyFile2290</div>
              <Link to="/dashboard/referrals" className="text-[13px] font-semibold text-[var(--accent)] no-underline whitespace-nowrap">
                <Gift size={14} className="inline -mt-0.5 mr-1" />
                Your referral credit →
              </Link>
            </div>
            <p className="text-[13px] text-[var(--muted)] mt-1 mb-3">
              Share the free 2290 calculators with your group, forum or association.
              {referral?.creditValue ? (
                <>
                  {" "}
                  When a friend who uses your link gets their first return accepted, you get a{" "}
                  <b>{money(referral.creditValue)}</b> credit off your next filing (up to {referral.capPerTaxPeriod} a
                  year; credits stack).
                </>
              ) : null}
            </p>
            <ShareBox refCode={referral?.code} placement="dashboard" title={null} intro={null} compact />
          </div>
        </div>
      </div>

      {rating && pending && (
        <FeedbackPanel
          key={pending.returnId}
          open
          returnId={pending.returnId}
          businessName={pending.businessName}
          refCode={referral?.code}
          onClose={() => {
            setRating(false);
            setPending(null);
          }}
        />
      )}
    </>
  );
}
